import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { UtilsService } from '../utils/utils.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentExportService {
  private readonly excelMimeType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  private readonly csvMimeType = 'text/csv;charset=utf-8';

  constructor(private utilsService: UtilsService) {}

  exportToExcel(
    data: Record<string, any>[],
    fileName: string = 'export.xlsx',
    sheetName: string = 'Sheet1',
  ) {
    if (!Array.isArray(data)) {
      throw new Error('Data provided to exportToExcel must be an array');
    }

    const sanitizedData = data.length ? data : [{}];
    const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: this.excelMimeType });

    const normalizedFileName = fileName.toLowerCase().endsWith('.xlsx')
      ? fileName
      : `${fileName}.xlsx`;

    saveAs(blob, normalizedFileName);
  }

  exportToCsv(
    data: Record<string, any>[],
    fileName: string = 'export.csv',
    sheetName: string = 'Sheet1',
  ) {
    if (!Array.isArray(data)) {
      throw new Error('Data provided to exportToCsv must be an array');
    }

    const sanitizedData = data.length ? data : [{}];
    const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const csvString = XLSX.write(workbook, {
      bookType: 'csv',
      type: 'string',
    }) as unknown as string;

    const blob = new Blob([csvString], { type: this.csvMimeType });
    const normalizedFileName = fileName.toLowerCase().endsWith('.csv')
      ? fileName
      : `${fileName}.csv`;

    saveAs(blob, normalizedFileName);
  }

  exportResultsToPdf(
    data: Record<string, any>[],
    fileName: string = 'export.pdf',
    title: string = 'Export',
    username: string = '',
  ) {
    if (!Array.isArray(data)) {
      throw new Error('Data provided to exportToPdf must be an array');
    }

    // Initialize fonts for pdfMake (handle different bundling shapes)
    const fontsAny: any = pdfFonts as any;
    const pdfMakeAny: any = pdfMake as any;
    if (!pdfMakeAny.vfs) {
      pdfMakeAny.vfs =
        fontsAny.pdfMake?.vfs || fontsAny.vfs || fontsAny.default?.vfs;
    }

    const docDefinition = this.prepareDocDefinitionForExportResultsToPdf(
      data,
      title,
      username,
    );

    const normalizedFileName = fileName.toLowerCase().endsWith('.pdf')
      ? fileName
      : `${fileName}.pdf`;

    pdfMake.createPdf(docDefinition).download(normalizedFileName);
  }

  private prepareDocDefinitionForExportResultsToPdf(
    data: Record<string, any>[],
    title: string,
    username: string,
  ) {
    const sanitizedData = data.length ? data : [{}];
    const headers = Object.keys(sanitizedData[0] || {});

    const body: any[] = [];
    if (headers.length) {
      body.push(
        headers.map((h) => ({
          text: String(h),
          bold: true,
          fillColor: '#eeeeee',
        })),
      );
    }

    sanitizedData.forEach((row) => {
      body.push(
        headers.map((h) => ({
          text: this.stringifyCell(row[h]),
          noWrap: false,
        })),
      );
    });

    const widths = headers.map(() => 70);

    const now = new Date();
    const formattedDate = format(now, 'MMM d y, h:mm:ss a');
    const footerText = `Generated by ${username} on ${formattedDate}`;

    return {
      pageOrientation: 'landscape',
      content: [
        {
          text: title,
          style: 'header',
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths,
            body,
          },
          layout: {
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,

            hLineWidth: () => 0,
            vLineWidth: () => 0,
          },
        },
      ],
      footer: (currentPage: number, pageCount: number) => {
        return {
          text: footerText,
          style: 'footer',
          alignment: 'left',
          margin: [32, 16, 32, 16],
        };
      },
      styles: {
        header: {
          fontSize: 12,
          bold: true,
        },
        footer: {
          fontSize: 8,
          color: '#666666',
        },
      },
      defaultStyle: {
        fontSize: 8,
      },
    };
  }

  private stringifyCell(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean')
      return String(value);

    return this.utilsService.formatDisplayValue(value);
  }
}
